/**
 * Strategy Calculator Module
 * 
 * This module implements the core business logic for race strategy calculations
 * following SOLID principles with separate classes for different responsibilities.
 */

import { 
  Strategy, 
  RaceEvent, 
  DriverData, 
  StintInfo, 
  PitStopDetails 
} from "../types/strategy";

/**
 * Single Responsibility Principle: Handles lap time calculations
 * This class is responsible for calculating total laps and laps per stint
 */
export class LapCalculator {
  /**
   * Calculate estimated total laps (NO PIT STOPS)
   * 
   * ⚠️ NOTE: This method is NOT used for actual strategy calculation!
   * Real calculation uses timeline simulation (see calculateStrategy method)
   * which accounts for pit stops, white flag timing, etc.
   * 
   * This is only for simple lap count estimation without pit stops.
   * 
   * iRacing White Flag Rule (simplified, no pits):
   * - Race: 6:00:00, Lap time: 1:47.5
   * - Lap 200 ends at 5:58:20 (200 × 107.5s = 21,500s)
   * - Lap 201 would end at 6:00:07.5 (21,607.5s)
   * - White flag on lap 201 (crosses after 6:00:00)
   * - Total: floor(21,600/107.5) + 1 = 200 + 1 = 201 laps
   * 
   * With pit stops: 197 laps (5 stops @ ~89s = ~445s pit time)
   * 
   * @param raceDuration - Race duration in minutes
   * @param avgLapTime - Average lap time in seconds (pure driving time)
   * @returns Estimated total laps (WITHOUT accounting for pit stops)
   */
  calculateTotalLaps(raceDuration: number, avgLapTime: number): number {
    if (!raceDuration || !avgLapTime || avgLapTime <= 0) {
      return 0;
    }
    
    const raceSeconds = raceDuration * 60;
    
    // Simplified: assumes NO PIT STOPS
    // Actual strategy uses timeline simulation instead
    const totalLaps = Math.floor(raceSeconds / avgLapTime) + 1;
    
    return totalLaps;
  }

  /**
   * Calculate estimated total laps for multi-class (NO PIT STOPS)
   * 
   * ⚠️ NOTE: Not used for actual strategy - timeline simulation is used instead
   * 
   * iRacing Multi-Class Rule:
   * - ALL classes complete the SAME number of laps as P1 (fastest class leader)
   * - White flag based on P1's timing
   * 
   * Example (no pit stops):
   * - 6:00:00 race, P1 lap: 1:30, Your lap: 1:45
   * - P1: floor(21,600/90) + 1 = 240 + 1 = 241 laps
   * - YOU: also 241 laps (same as P1, regardless of your lap time)
   * 
   * @param raceDuration - Race duration in minutes
   * @param yourLapTime - Your class lap time in seconds  
   * @param fastestClassLapTime - Fastest class leader lap time in seconds
   * @returns Estimated total laps (WITHOUT pit stops)
   */
  calculateMultiClassLaps(raceDuration: number, yourLapTime: number, fastestClassLapTime: number): number {
    if (!raceDuration || !yourLapTime || !fastestClassLapTime || yourLapTime <= 0 || fastestClassLapTime <= 0) {
      return this.calculateTotalLaps(raceDuration, yourLapTime);
    }
    
    const raceSeconds = raceDuration * 60;

    // Simplified: P1 lap count without pit stops
    const p1TotalLaps = Math.floor(raceSeconds / fastestClassLapTime) + 1;

    // Multi-class: everyone completes same lap count as P1
    return p1TotalLaps;
  }

  /**
   * Calculate how many laps can be driven on a full tank of fuel
   * @param fuelCapacity - Fuel tank capacity in liters
   * @param fuelConsumption - Fuel consumption per lap in liters
   * @returns Number of laps per stint
   */
  calculateLapsPerStint(fuelCapacity: number, fuelConsumption: number): number {
    if (!fuelCapacity || !fuelConsumption || fuelConsumption <= 0) {
      return 0;
    }
    // Reserve 0.8L safety margin (need fuel to complete pit lap)
    const safetyMargin = 0.8;
    const usableFuel = fuelCapacity - safetyMargin;
    return Math.floor(usableFuel / fuelConsumption);
  }
}

/**
 * Single Responsibility Principle: Handles stint calculations and breakdowns
 * This class manages stint planning and creates detailed stint information
 */
export class StintCalculator {
  /**
   * Calculate the total number of stints needed for the race
   * @param totalLaps - Total race laps
   * @param lapsPerStint - Laps that can be driven per tank
   * @returns Number of stints required
   */
  calculateStints(totalLaps: number, lapsPerStint: number): number {
    if (!totalLaps || !lapsPerStint || lapsPerStint <= 0) {
      return 0;
    }
    return Math.ceil(totalLaps / lapsPerStint);
  }

  /**
   * Create a detailed breakdown of all stints in the race
   * @param totalLaps - Total race laps
   * @param lapsPerStint - Laps per stint
   * @param avgLapTime - Average lap time in seconds
   * @param fuelConsumption - Fuel consumption per lap
   * @param fuelCapacity - Fuel tank capacity in liters
   * @param pitStopDetails - Pit stop timing details
   * @returns Array of stint information
   */
  calculateStintBreakdown(
    totalLaps: number, 
    _lapsPerStint: number, 
    avgLapTime: number,
    fuelConsumption: number,
    fuelCapacity: number,
    pitStopDetails: PitStopDetails
  ): StintInfo[] {
    const stints: StintInfo[] = [];
    let remainingLaps = totalLaps;

    // Create stints until all laps are accounted for
    for (let stintNumber = 1; remainingLaps > 0; stintNumber++) {
      // Calculate maximum laps that can be driven within fuel capacity
      // Use a more fuel-efficient calculation that allows us to use almost all fuel
      /* const maxLapsInStint = Math.floor(fuelCapacity / fuelConsumption); */
      
      // For normal strategy, use fuel-limited laps per stint
      // This ensures we pit when fuel is low, not after a fixed number of laps
      // Enforce safety margin: must never plan below 0.8L remaining (need fuel to complete pit lap)
      const safetyMargin = 0.8;
      const usableFuel = fuelCapacity - safetyMargin;
      const maxLapsWithSafety = Math.floor(usableFuel / fuelConsumption);
      
      const actualLapsInStint = Math.min(maxLapsWithSafety, remainingLaps);
      
      // Debug: Stint calculation details (removed for production)
      
      // Calculate fuel used based on actual laps driven
      const actualFuelUsed = actualLapsInStint * fuelConsumption;
      
      const stintTime = actualLapsInStint * avgLapTime;
      const pitStopAfter = remainingLaps > actualLapsInStint;
      
      // Calculate pit stop time based on actual refuel amount needed
      let pitStopTime = 0;
      if (pitStopAfter) {
        // Determine if next stint is the final stint and fuel accordingly (splash-and-dash)
        const lapsRemainingAfterThisStint = remainingLaps - actualLapsInStint;
        const nextStintMaxLaps = Math.floor((fuelCapacity - safetyMargin) / fuelConsumption);
        const plannedNextStintLaps = Math.min(nextStintMaxLaps, lapsRemainingAfterThisStint);
        const nextStintIsFinal = plannedNextStintLaps === lapsRemainingAfterThisStint;

        const currentFuelLevel = fuelCapacity - actualFuelUsed;
        const targetFuelLevel = nextStintIsFinal
          ? Math.min(fuelCapacity, plannedNextStintLaps * fuelConsumption + safetyMargin)
          : fuelCapacity;
        const rawFuelToAdd = targetFuelLevel - currentFuelLevel;
        const fuelToAdd = Math.max(0, Math.min(rawFuelToAdd, fuelCapacity - currentFuelLevel));
        
        // Calculate refuel time based on fuel to add
        const refuelTime = fuelToAdd * pitStopDetails.refuelTime;
        pitStopTime = pitStopDetails.pitInTime + refuelTime + pitStopDetails.pitOutTime;
      }

      stints.push({
        stintNumber,
        laps: actualLapsInStint,
        fuelUsed: actualFuelUsed,
        time: stintTime,
        pitStopAfter,
        pitStopTime
      });

      remainingLaps -= actualLapsInStint;
    }

    // Add extra lap suggestions for each stint (except the last one)
    const fuelSaveCalc = new FuelSaveCalculator();
    for (let i = 0; i < stints.length - 1; i++) {
      const stint = stints[i];
      const fuelRemaining = fuelCapacity - stint.fuelUsed;
      
      // Check if we're within 30% of being able to do one more lap (including 0.6L safety margin)
      const safetyMargin = 0.6;
      const fuelNeededForExtraLap = fuelConsumption + safetyMargin;
      const fuelShortfall = fuelNeededForExtraLap - fuelRemaining;
      
      if (fuelShortfall > 0 && fuelShortfall <= fuelConsumption * 0.3) {
        const suggestion = fuelSaveCalc.calculateFuelSavingForExtraLap(
          fuelRemaining,
          fuelConsumption,
          stint.laps,
          pitStopDetails.refuelTime
        );
        
        // Only add suggestion if it's achievable (not impossible)
        if (suggestion.severity !== 'impossible') {
          stints[i].extraLapSuggestion = suggestion;
        }
      }
    }

    return stints;
  }
}

/**
 * Single Responsibility Principle: Analyzes fuel saving strategies
 * 
 * CORRECTED FUEL SAVE LOGIC:
 * This class calculates whether fuel saving is faster than making a pit stop.
 * 
 * Decision Framework:
 * 1. Calculate time cost of fuel saving (lap time penalty × remaining laps)
 * 2. Calculate time cost of pit stop (pit delta in seconds)
 * 3. Fuel saving is recommended if: fuel_save_time_cost < pit_stop_time_cost
 * 
 * Fuel Save Penalty (Research-Based):
 * - ~0.15 seconds per lap per 1% fuel reduction
 * - 5% fuel save ≈ 0.75s/lap
 * - 10% fuel save ≈ 1.5s/lap
 * - >15% fuel save = unrealistic in most cars
 * 
 * Example:
 * - 40 laps remaining, need to save 2% fuel per lap
 * - Fuel save cost: 2% × 0.15s × 40 laps = 12 seconds
 * - Pit stop cost: 30 seconds
 * - Decision: SAVE FUEL (12s < 30s) → Gain 18 seconds!
 */
export class FuelSaveCalculator {
  constructor() {}

  /**
   * Calculate how much fuel saving would allow one more lap before pitting
   * 
   * IMPORTANT: Maintains 0.6L safety margin when pitting (car slows down below 0.6L)
   * 
   * @param fuelRemaining - Fuel remaining at end of stint in liters
   * @param avgFuelPerLap - Average fuel consumption per lap in liters
   * @param lapsCompletedInStint - Number of laps completed in this stint
   * @param refuelTimePerLiter - Time to refuel one liter in seconds
   * @returns Suggestion for fuel saving to complete one extra lap
   */
  calculateFuelSavingForExtraLap(
    fuelRemaining: number,
    avgFuelPerLap: number,
    lapsCompletedInStint: number,
    refuelTimePerLiter: number
  ): import("../types/strategy").ExtraLapSuggestion {
    // Safety margin: car slows down below 0.6L
    const safetyMargin = 0.6;
    
    // How much fuel do we need for one more lap + safety margin?
    const fuelNeededForExtraLap = avgFuelPerLap + safetyMargin;
    const fuelShortfall = fuelNeededForExtraLap - fuelRemaining;
    
    // Calculate pit time saved by doing one extra lap (less fuel to add at next stop)
    // If we do one more lap, we need avgFuelPerLap less fuel at the next pit stop
    const pitTimeSaved = avgFuelPerLap * refuelTimePerLiter;
    
    // Can we already do the extra lap with safety margin?
    if (fuelShortfall <= 0) {
      return {
        canDoExtraLap: true,
        fuelShortfall: 0,
        fuelSavePerLap: 0,
        fuelSavePercentage: 0,
        pitTimeSaved,
        message: 'You already have enough fuel for one more lap (with 0.6L safety margin)!',
        severity: 'easy',
      };
    }
    
    // How much fuel saving per lap would we need?
    const fuelSavePerLap = fuelShortfall / lapsCompletedInStint;
    const fuelSavePercentage = (fuelSavePerLap / avgFuelPerLap) * 100;
    
    // Determine severity and message
    if (fuelSavePercentage <= 3) {
      return {
        canDoExtraLap: true,
        fuelShortfall,
        fuelSavePerLap,
        fuelSavePercentage,
        pitTimeSaved,
        message: `💡 Save ${fuelSavePerLap.toFixed(3)}L per lap (${fuelSavePercentage.toFixed(1)}%) for one extra lap! Very achievable.`,
        severity: 'easy',
      };
    } else if (fuelSavePercentage <= 7) {
      return {
        canDoExtraLap: true,
        fuelShortfall,
        fuelSavePerLap,
        fuelSavePercentage,
        pitTimeSaved,
        message: `⚠️ Save ${fuelSavePerLap.toFixed(3)}L per lap (${fuelSavePercentage.toFixed(1)}%) for one extra lap. Challenging but possible.`,
        severity: 'moderate',
      };
    } else if (fuelSavePercentage <= 12) {
      return {
        canDoExtraLap: true,
        fuelShortfall,
        fuelSavePerLap,
        fuelSavePercentage,
        pitTimeSaved,
        message: `🔥 Save ${fuelSavePerLap.toFixed(3)}L per lap (${fuelSavePercentage.toFixed(1)}%) for one extra lap. Very difficult.`,
        severity: 'hard',
      };
    } else {
      return {
        canDoExtraLap: false,
        fuelShortfall,
        fuelSavePerLap,
        fuelSavePercentage,
        pitTimeSaved,
        message: `❌ Would need ${fuelSavePercentage.toFixed(1)}% fuel saving - not realistic. Pit as planned.`,
        severity: 'impossible',
      };
    }
  }

  /**
   * CORRECTED: Determine if fuel saving is faster than pitting
   * 
   * @param fuelSavePercentage - Percentage of fuel to save per lap
   * @param remainingLaps - Number of laps remaining
   * @param pitStopDelta - Time lost making a pit stop (seconds)
   * @returns Decision with time comparison
   */
  isFuelSavingFaster(
    fuelSavePercentage: number,
    remainingLaps: number,
    pitStopDelta: number
  ): {
    decision: 'save' | 'pit' | 'unrealistic';
    fuelSaveTimeCost: number;
    pitStopTimeCost: number;
    timeAdvantage: number; // positive = fuel save wins, negative = pit wins
    maxLapTimeLoss: number; // max seconds per lap you can lose
  } {
    // Unrealistic fuel saving threshold
    if (fuelSavePercentage > 15) {
      return {
        decision: 'unrealistic',
        fuelSaveTimeCost: Infinity,
        pitStopTimeCost: pitStopDelta,
        timeAdvantage: -Infinity,
        maxLapTimeLoss: 0,
      };
    }

    // Calculate fuel save time cost
    // Research: ~0.15s per lap per 1% fuel reduction
    const lapTimePenaltyPerPercent = 0.15;
    const lapTimePenalty = fuelSavePercentage * lapTimePenaltyPerPercent;
    const fuelSaveTimeCost = lapTimePenalty * remainingLaps;

    // Pit stop time cost
    const pitStopTimeCost = pitStopDelta;

    // Maximum lap time loss allowed (can lose this much per lap and still beat pit stop)
    const maxLapTimeLoss = pitStopTimeCost / remainingLaps;

    // Time advantage (positive = fuel save is faster)
    const timeAdvantage = pitStopTimeCost - fuelSaveTimeCost;

    // Decision: fuel save is faster if it costs less time
    const decision = fuelSaveTimeCost < pitStopTimeCost ? 'save' : 'pit';

    return {
      decision,
      fuelSaveTimeCost,
      pitStopTimeCost,
      timeAdvantage,
      maxLapTimeLoss,
    };
  }

  /**
   * Calculate fuel saving strategy to avoid a final short stint
   * @param raceDuration - Race duration in minutes
   * @param totalLaps - Total race laps
   * @param lapsPerStint - Laps per stint
   * @param stints - Number of stints
   * @param fuelConsumption - Fuel consumption per lap
   * @param pitStopDetails - Pit stop timing details
   * @param avgLapTime - Average lap time
   * @param fuelCapacity - Fuel tank capacity
   * @param userActualLapTime - User's actual achievable lap time
   * @returns Fuel saving analysis or null if not applicable
   */
  calculateFuelSaveStrategy(
    raceDuration: number,
    normalStrategyTotalLaps: number,
    normalStrategyLapsPerStint: number,
    normalStrategyStints: number,
    fuelConsumption: number,
    pitStopDetails: PitStopDetails,
    avgLapTime: number,
    fuelCapacity: number,
    userActualLapTime?: number | null,
    userActualFuelConsumption?: number | null
  ) {
    // Debug: Fuel save strategy inputs (removed for production)
    
    // Calculate laps in the final stint
    const lastStintLaps = normalStrategyTotalLaps % normalStrategyLapsPerStint;
    // Debug: Last stint laps (removed for production)
    
    // If no remainder, no fuel saving is needed
    if (lastStintLaps === 0) {
      // No remainder; fuel saving typically not needed
      // return null;
    }

    // Step 1: Calculate time spent in pit for the splash and dash stint
    // The splash-and-dash only needs fuel for the remaining laps, not a full tank
    const splashAndDashFuelNeeded = lastStintLaps * fuelConsumption;
    const splashAndDashRefuelTime = splashAndDashFuelNeeded * pitStopDetails.refuelTime;
    const splashAndDashPitTime = pitStopDetails.pitInTime + splashAndDashRefuelTime + pitStopDetails.pitOutTime;
    
    // CORRECTED APPROACH:
    // To avoid splash-and-dash, we need to drive those extra laps in previous stints
    // Example: 13 extra laps ÷ 5 stints = 2.6 extra laps per stint
    
    const previousStints = normalStrategyStints - 1; // Stints where we can fuel save
    const extraLapsPerStint = lastStintLaps / previousStints;
    
    // New laps per stint with fuel saving: 37 + 2.6 = 39.6 laps
    const newLapsPerStint = normalStrategyLapsPerStint + extraLapsPerStint;
    
    // Calculate fuel saving needed per lap
    // Old: 37 laps on 120L → 3.243L/lap
    // New: 39.6 laps on 120L → 3.030L/lap
    // Fuel save: 3.243 - 3.030 = 0.213L/lap
    const fuelPerStint = fuelCapacity - 0.9; // Usable fuel per stint
    const newFuelPerLap = fuelPerStint / newLapsPerStint;
    const fuelSavePerLap = fuelConsumption - newFuelPerLap;
    
    // CORRECTED: Calculate actual time budget by comparing total pit times
    // Normal strategy: (normalStrategyStints - 1) pit stops with full refuels + 1 splash-and-dash
    const normalFullPitStops = normalStrategyStints - 2; // All except last two stints
    const normalFullRefuelAmount = fuelPerStint; // Full tank refuel
    const normalFullPitTime = pitStopDetails.pitInTime + (normalFullRefuelAmount * pitStopDetails.refuelTime) + pitStopDetails.pitOutTime;
    const normalTotalPitTime = (normalFullPitStops * normalFullPitTime) + splashAndDashPitTime;
    
    // Fuel save strategy: (previousStints - 1) pit stops with full refuels
    const fuelSavePitStops = previousStints - 1;
    const fuelSaveFullPitTime = pitStopDetails.pitInTime + (normalFullRefuelAmount * pitStopDetails.refuelTime) + pitStopDetails.pitOutTime;
    const fuelSaveTotalPitTime = fuelSavePitStops * fuelSaveFullPitTime;
    
    // Time budget: difference in total pit time minus target advantage
    // We want fuel save to be faster, so we build in a safety margin
    const rawTimeBudget = normalTotalPitTime - fuelSaveTotalPitTime;
    const targetAdvantage = 10; // Target: fuel save should be at least 10s faster
    const totalTimeBudget = Math.max(0, rawTimeBudget - targetAdvantage);
    
    // Maximum time loss per lap: budget ÷ total laps with fuel saving
    const totalLapsInNormalPreviousStints = normalStrategyTotalLaps - lastStintLaps;
    const maxTimeLossPerLap = totalTimeBudget / totalLapsInNormalPreviousStints;
    
    // Calculate the maximum lap time target
    const maxLapTimeTarget = avgLapTime + maxTimeLossPerLap;
    
    // Calculate the refuel amount at the last pit stop (before splash-and-dash stint)
    const splashAndDashRefuelAmount = splashAndDashFuelNeeded;
    
    // Step 2: Calculate amount of fuel that needs to be saved per stint
    const fuelToSavePerStint = fuelSavePerLap * normalStrategyLapsPerStint;
    
    // Step 3: Already calculated above
    const netTimeSaved = totalTimeBudget; // This is our budget
    
    // Calculate fuel save strategy total laps (same as normal strategy)
    const fuelSaveTotalLaps = normalStrategyTotalLaps;
    
    // CORRECTED: Use the new fuel consumption calculated above
    const finalAdjustedFuelConsumption = newFuelPerLap;
    
    // Recalculate laps per stint using the final adjusted fuel consumption
    const finalCalculatedLapsPerStint = Math.floor(fuelPerStint / finalAdjustedFuelConsumption);
    
    // Debug: Fuel consumption computation details (removed for production)

    // Calculate a realistic time loss estimate (conservative approach)
    const realisticTimeLossPerLap = maxTimeLossPerLap * 0.3; // Use 30% of max time loss for more realistic estimate
    
    // Determine which lap time to use for stint calculations
    // If user provided their actual achievable lap time, use that; otherwise use the calculated max target
    const lapTimeForStintCalculations = (userActualLapTime && userActualLapTime > 0) 
      ? userActualLapTime 
      : maxLapTimeTarget;
    
    // Determine which fuel consumption to use for stint calculations
    // If user provided their actual fuel consumption, use that; otherwise use the calculated adjusted fuel
    const fuelConsumptionForStintCalculations = (userActualFuelConsumption && userActualFuelConsumption > 0)
      ? userActualFuelConsumption
      : finalAdjustedFuelConsumption;
    
    console.log("🔧 FUEL SAVE LAP TIME DECISION:", {
      userActualLapTime,
      maxLapTimeTarget,
      lapTimeForStintCalculations,
      willUseUserInput: !!(userActualLapTime && userActualLapTime > 0),
      userActualFuelConsumption,
      finalAdjustedFuelConsumption,
      fuelConsumptionForStintCalculations,
      willUseUserFuel: !!(userActualFuelConsumption && userActualFuelConsumption > 0)
    });
    
    // Calculate the adjusted strategy (without splash-and-dash)
    const adjustedStrategy = this.calculateAdjustedStrategy(
      raceDuration, // Pass race duration instead of total laps
      fuelConsumption, // Use original fuel consumption
      maxLapTimeTarget, // Use max lap time target for fuel save strategy
      realisticTimeLossPerLap, // Use realistic time loss
      maxTimeLossPerLap, // Pass max time loss per lap
      pitStopDetails,
      fuelCapacity,
      normalStrategyStints,
      fuelConsumptionForStintCalculations, // Pass fuel consumption (user input or calculated)
      lapTimeForStintCalculations, // Pass the actual lap time (user input or calculated target)
      fuelSaveTotalLaps, // Pass the fuel save strategy total laps
      finalCalculatedLapsPerStint // Pass the final calculated laps per stint for fuel save strategy
    );



    // CORRECTED: Calculate time advantage at finish line
    // Both strategies complete the SAME number of laps (race ends when leader finishes)
    
    // Time saved by avoiding splash-and-dash pit stop
    const timeSavedFromAvoidingPit = splashAndDashPitTime;
    
    // Time cost of fuel saving depends on user's actual achievable lap time
    let fuelSaveTimeCost: number;
    let actualLapTimeWhileFuelSaving: number;
    
    if (userActualLapTime && userActualLapTime > 0) {
      // User provided their actual achievable lap time while fuel saving
      actualLapTimeWhileFuelSaving = userActualLapTime;
    } else {
      // No user input - assume they drive at max lap time target
      actualLapTimeWhileFuelSaving = maxLapTimeTarget;
    }
    
    // Calculate time cost: how much slower they drive while fuel saving
    const lapTimeDifference = actualLapTimeWhileFuelSaving - avgLapTime;
    const totalLapsWithFuelSaving = normalStrategyTotalLaps - lastStintLaps;
    fuelSaveTimeCost = lapTimeDifference * totalLapsWithFuelSaving;
    
    // Time advantage = Time saved from pit - Time lost from slower laps
    // Positive = fuel save is faster, Negative = pit stop is faster
    let timeAdvantageAtFinish = timeSavedFromAvoidingPit - fuelSaveTimeCost;
    
    // Lap advantage: difference between fuel save laps and normal strategy laps
    // Positive = more laps completed with fuel save, Negative = fewer laps
    const lapAdvantage = adjustedStrategy.totalLaps - normalStrategyTotalLaps;
    
    // Safety check for NaN
    if (isNaN(timeAdvantageAtFinish)) {
      timeAdvantageAtFinish = 0;
    }

    // Calculate detailed last stint analysis for both strategies
    /* const normalStrategyLastStintAnalysis = this.calculateLastStintAnalysis(
      normalStrategyTotalLaps,
      avgLapTime,
      pitStopDetails.totalTime,
      normalStrategyStints - 1, // Number of pit stops
      "Normal Strategy"
    ); */
    
    /* const fuelSaveStrategyLastStintAnalysis = this.calculateLastStintAnalysis(
      adjustedStrategy.totalLaps,
      avgLapTime, // Use original lap time for fuel save strategy
      pitStopDetails.totalTime,
      adjustedStrategy.pitStops,
      "Fuel Save Strategy"
    ); */
    
    // Debug: Last stint analysis tables (removed for production)

    // Note: Detailed lap-by-lap breakdown is now available in the expandable stint breakdown in the UI
    
    // Debug: Fuel save analysis summary (removed for production)

    // Add the fuel save analysis to the adjusted strategy
    adjustedStrategy.fuelSave = {
      requiredFuelSave: fuelToSavePerStint,
      maxTimeLossPerLap: maxTimeLossPerLap,
      estimatedTimeLossPerLap: realisticTimeLossPerLap, // Use realistic time loss
      timeSaved: netTimeSaved, // NET time saved (splash-and-dash avoided - extra pit time)
      isWorthwhile: true, // Always show the analysis if applicable
      adjustedFuelConsumption: finalAdjustedFuelConsumption,
      adjustedLapTime: maxLapTimeTarget, // Fixed calculated max lap time target
      lapAdvantage: lapAdvantage, // Number of extra laps completed
      timeAdvantageAtFinish: timeAdvantageAtFinish, // Time advantage at finish line
      splashAndDashRefuelAmount: splashAndDashRefuelAmount, // Fuel refueled at last pit stop before splash-and-dash
      splashAndDashRefuelTime: splashAndDashPitTime, // Time spent in pit for splash-and-dash refuel
      adjustedStrategy
    };

    return {
      requiredFuelSave: fuelToSavePerStint,
      maxTimeLossPerLap: maxTimeLossPerLap,
      estimatedTimeLossPerLap: realisticTimeLossPerLap, // Use realistic time loss
      timeSaved: netTimeSaved, // NET time saved (splash-and-dash avoided - extra pit time)
      isWorthwhile: true, // Always show the analysis if applicable
      adjustedFuelConsumption: finalAdjustedFuelConsumption,
      adjustedLapTime: maxLapTimeTarget, // Fixed calculated max lap time target
      lapAdvantage: lapAdvantage, // Number of extra laps completed
      timeAdvantageAtFinish: timeAdvantageAtFinish, // Time advantage at finish line
      splashAndDashRefuelAmount: splashAndDashRefuelAmount, // Fuel refueled at last pit stop before splash-and-dash
      splashAndDashRefuelTime: splashAndDashPitTime, // Time spent in pit for splash-and-dash refuel
      adjustedStrategy
    };
  }

  // Removed unused calculateLastStintAnalysis testing helper

  /**
   * Estimate time loss per lap when fuel saving
   * @param fuelToSavePerStint - Fuel to save per stint
   * @param fuelConsumption - Normal fuel consumption per lap
   * @param splashAndDashPitTime - Time for the splash-and-dash pit stop
   * @param totalLapsInPreviousStints - Total laps in stints where we can fuel save
   * @returns Estimated time loss per lap in seconds
   */
  /* private _estimateTimeLossPerLap(
    _fuelToSavePerStint: number, 
    _fuelConsumption: number,
    _splashAndDashPitTime: number,
    _totalLapsInPreviousStints: number
  ): number {
    // The maximum time loss per lap we can afford is the splash-and-dash pit time
    // divided by the total number of laps in previous stints
    const maxTimeLossPerLap = _splashAndDashPitTime / _totalLapsInPreviousStints;
    
    // For fuel saving, we'll use a conservative estimate of 80% of the maximum
    // This accounts for the fact that fuel saving doesn't always result in maximum time loss
    const estimatedTimeLossPerLap = maxTimeLossPerLap * 0.8;
    
    return estimatedTimeLossPerLap;
  } */

  /**
   * Calculate the adjusted strategy with fuel saving applied
   * @param raceDuration - Race duration in minutes
   * @param originalFuelConsumption - Original fuel consumption per lap
   * @param originalLapTime - Original lap time
   * @param estimatedTimeLossPerLap - Time loss per lap due to fuel saving
   * @param maxTimeLossPerLap - Maximum time loss per lap
   * @param pitStopDetails - Pit stop timing details
   * @param fuelCapacity - Fuel tank capacity
   * @param originalStints - Number of stints in the normal strategy
  /**
   * Calculate adjusted strategy with fuel saving (avoiding splash-and-dash)
   * 
   * CRITICAL: Both normal and fuel-save strategies finish at the SAME race end time!
   * The race ends when the LEADER completes their white flag lap.
   * 
   * Time advantage = Pit stop avoided - (Lap time difference × Laps with fuel saving)
   * 
   * @param raceDuration - Race duration in minutes
   * @param _originalFuelConsumption - Original fuel consumption
   * @param _originalLapTime - Original lap time
   * @param _estimatedTimeLossPerLap - Estimated time loss per lap
   * @param _maxTimeLossPerLap - Maximum time loss per lap
   * @param pitStopDetails - Pit stop timing details
   * @param fuelCapacity - Fuel tank capacity
   * @param originalStints - Number of stints in normal strategy
   * @param adjustedFuelConsumption - Fuel consumption with saving applied
   * @param lapTimeTarget - Target lap time (slower due to fuel saving)
   * @param _fuelSaveTotalLaps - Total laps for fuel save strategy
   * @param lapsPerStintInFuelSave - Laps per stint in fuel save strategy
   * @returns Adjusted strategy without splash-and-dash stint
   */
  private calculateAdjustedStrategy(
    raceDuration: number,
    _originalFuelConsumption: number,
    _originalLapTime: number,
    _estimatedTimeLossPerLap: number,
    _maxTimeLossPerLap: number,
    pitStopDetails: PitStopDetails,
    fuelCapacity: number,
    originalStints: number,
    adjustedFuelConsumption: number,
    lapTimeTarget: number,
    _fuelSaveTotalLaps: number,
    lapsPerStintInFuelSave: number
): Strategy {
    // Fuel saving strategy should have one less stint than the normal strategy
    const adjustedStints = originalStints - 1;
    const adjustedPitStops = adjustedStints - 1;
    
    // IMPORTANT: Simulate the fuel save timeline with WHITE FLAG RULE
    // The race ends when the LEADER finishes, but this driver gets the white flag
    // if they START a lap before the race time expires
    
    // Simulate the fuel save strategy timeline to find actual laps completed
    const raceSeconds = raceDuration * 60;
    let simTime = 0;
    let simLaps = 0;
    let currentStint = 0;
    
    // Calculate fuel per stint for pit stop calculations
    const fuelPerStint = fuelCapacity - 0.9;
    
    // Keep racing until time runs out
    while (true) {
      currentStint++;
      
      // Complete laps in this stint until we run out of fuel or time
      let lapsInCurrentStint = 0;
      const maxLapsPerStint = Math.floor(fuelPerStint / adjustedFuelConsumption);
      
      while (lapsInCurrentStint < maxLapsPerStint) {
        // WHITE FLAG RULE: If remaining time < lap time when STARTING this lap,
        // this is the final lap (white flag) - complete it and end race
        const remainingTime = raceSeconds - simTime;
        
        if (remainingTime < lapTimeTarget) {
          // White flag! Complete this final lap and exit
          simTime += lapTimeTarget;
          simLaps++;
          // Race is over after white flag lap
          return this.createStrategyFromSimulation(simLaps, adjustedStints, adjustedPitStops, simTime, fuelCapacity, pitStopDetails, lapTimeTarget, lapsPerStintInFuelSave, _fuelSaveTotalLaps, adjustedFuelConsumption);
        }
        
        // Normal lap - complete it
        simTime += lapTimeTarget;
        simLaps++;
        lapsInCurrentStint++;
      }
      
      // Stint complete - need to pit (unless this would be after the race ends)
      // Check if we have time for a pit stop
      const refuelAmount = fuelPerStint;
      const refuelTime = refuelAmount * pitStopDetails.refuelTime;
      const pitTime = pitStopDetails.pitInTime + refuelTime + pitStopDetails.pitOutTime;
      
      // Only pit if we have time for at least one more lap after the pit stop
      if (simTime + pitTime + lapTimeTarget > raceSeconds) {
        // Not enough time for pit + another lap - race ends here
        break;
      }
      
      simTime += pitTime;
    }
    
    // All laps completed - race time has expired
    const adjustedTotalLaps = simLaps;
    const actualStints = currentStint;
    const actualPitStops = currentStint - 1;
    
    return this.createStrategyFromSimulation(adjustedTotalLaps, actualStints, actualPitStops, simTime, fuelCapacity, pitStopDetails, lapTimeTarget, lapsPerStintInFuelSave, _fuelSaveTotalLaps, adjustedFuelConsumption);
  }
  
  // Helper function to create strategy from simulation results
  private createStrategyFromSimulation(
    adjustedTotalLaps: number,
    adjustedStints: number,
    adjustedPitStops: number,
    _simTime: number,
    fuelCapacity: number,
    pitStopDetails: PitStopDetails,
    lapTimeTarget: number,
    lapsPerStintInFuelSave: number,
    _fuelSaveTotalLaps: number,
    adjustedFuelConsumption: number
  ): Strategy {
    // Use the calculated laps per stint from the fuel save strategy calculation
    const lapsPerStint = lapsPerStintInFuelSave;
    
    console.log("Fuel Save Strategy Laps Per Stint Calculation:", {
      fuelCapacity,
      adjustedFuelConsumption,
      lapsPerStintInFuelSave,
      lapsPerStint,
      normalStrategyLaps: _fuelSaveTotalLaps,
      actualCompletedLaps: adjustedTotalLaps,
      lapTimeUsed: lapTimeTarget,
      adjustedStints
    });
    
    // Create adjusted stint breakdown with evenly distributed extra laps
    const adjustedStintBreakdown: StintInfo[] = [];
    let remainingLaps = adjustedTotalLaps;

    // Calculate how many extra laps we need to distribute
    const baselineLapsTotal = lapsPerStint * adjustedStints; // If all stints were equal
    const extraLaps = adjustedTotalLaps - baselineLapsTotal; // Extra laps to distribute
    
    // Distribute extra laps evenly across stints
    // Example: 13 extra laps ÷ 5 stints = 2.6 → some stints get +3, others get +2
    const extraLapsPerStint = Math.floor(extraLaps / adjustedStints); // 13 ÷ 5 = 2
    const stintsWithExtraLap = extraLaps % adjustedStints; // 13 % 5 = 3 stints get +1 more

    for (let stintNumber = 1; remainingLaps > 0; stintNumber++) {
      // Calculate laps for this stint with even distribution
      let lapsInStint: number;
      
      if (stintNumber === adjustedStints) {
        // Last stint gets all remaining laps (should be very close to others now)
        lapsInStint = remainingLaps;
      } else {
        // Distribute extra laps evenly
        // First N stints get an extra lap, where N = stintsWithExtraLap
        const bonusLap = stintNumber <= stintsWithExtraLap ? 1 : 0;
        lapsInStint = Math.min(lapsPerStint + extraLapsPerStint + bonusLap, remainingLaps);
      }
      
      // Calculate fuel consumption per lap for this stint with 0.9L safety margin
      const fuelPerStint = fuelCapacity - 0.9;
      const fuelConsumptionPerLap = fuelPerStint / lapsInStint;
      
      // Calculate fuel used in this stint
      const fuelUsed = lapsInStint * fuelConsumptionPerLap;
      
      // Calculate stint time: use the target lap time (Max Lap Time Target) for fuel save strategy
      // This reflects the slower pace needed to save fuel
      const stintTime = lapsInStint * lapTimeTarget;
      
      const pitStopAfter = remainingLaps > lapsInStint;
      
      // Calculate pit stop time based on actual refuel amount needed
      let pitStopTime = 0;
      if (pitStopAfter) {
        // Calculate how much fuel we need to add to the tank
        // We pit in with some fuel left, so we need to refuel to full capacity
        const currentFuelLevel = fuelCapacity - fuelUsed;
        const fuelToAdd = Math.min(fuelCapacity - currentFuelLevel, fuelCapacity); // Don't exceed tank capacity
        
        // Calculate refuel time based on fuel to add
        const refuelTime = fuelToAdd * pitStopDetails.refuelTime;
        pitStopTime = pitStopDetails.pitInTime + refuelTime + pitStopDetails.pitOutTime;
      }

      console.log(`🔧 FUEL SAVE PIT STOP DEBUG - Stint ${stintNumber}:`, {
        'Fuel Capacity': fuelCapacity,
        'Fuel Used': fuelUsed,
        'Current Fuel Level': fuelCapacity - fuelUsed,
        'Fuel To Add': fuelCapacity - (fuelCapacity - fuelUsed),
        'Refuel Rate (s/L)': pitStopDetails.refuelTime,
        'Refuel Time': (fuelCapacity - (fuelCapacity - fuelUsed)) * pitStopDetails.refuelTime,
        'Pit In Time': pitStopDetails.pitInTime,
        'Pit Out Time': pitStopDetails.pitOutTime,
        'TOTAL PIT TIME': pitStopTime,
        'Expected vs Actual': `Expected: ~${Math.round((fuelCapacity - 1.6) * pitStopDetails.refuelTime + 30)}s | Actual: ${pitStopTime}s`
      });

      adjustedStintBreakdown.push({
        stintNumber,
        laps: lapsInStint,
        fuelUsed: fuelUsed,
        time: stintTime,
        pitStopAfter,
        pitStopTime
      });

      remainingLaps -= lapsInStint;
    }

    // CORRECTED: Calculate total race time for fuel save strategy
    // The race ends at the SAME time as normal strategy (when leader finishes white flag lap)
    // We use the slower lap time to calculate stint times, then sum everything up
    
    const totalStintTime = adjustedStintBreakdown.reduce((sum, stint) => sum + stint.time, 0);
    const totalPitTime = adjustedStintBreakdown.reduce((sum, stint) => sum + stint.pitStopTime, 0);
    const actualRaceEndTime = totalStintTime + totalPitTime;
    
    return {
      totalLaps: adjustedTotalLaps,
      lapsPerStint: lapsPerStint,
      stints: adjustedStints,
      pitStops: adjustedPitStops,
      totalTime: actualRaceEndTime, // Sum of stint times + pit times
      fuelSave: null, // No fuel save analysis in the adjusted strategy
      stintBreakdown: adjustedStintBreakdown
    };
  }

  /**
   * Calculate detailed lap-by-lap breakdown for testing purposes with smart refueling
   * @param totalLaps - Total laps in the race
   * @param avgLapTime - Average lap time in seconds
   * @param pitStopTime - Time per pit stop in seconds
   * @param pitStops - Number of pit stops
   * @param strategyName - Name of the strategy for display
   * @param fuelCapacity - Fuel tank capacity in liters
   * @param fuelConsumption - Fuel consumption per lap in liters
   * @returns Array of lap details
   */
  /* private _calculateLapBreakdownWithSmartRefuel(
    totalLaps: number,
    avgLapTime: number,
    pitStopTime: number,
    pitStops: number,
    strategyName: string,
    fuelCapacity: number = 120,
    fuelConsumption: number = 3.2
  ): any[] {
    const breakdown = [];
    let currentTime = 0; // Start at 00:00:00
    let pitStopCount = 0;
    let currentFuel = fuelCapacity; // Start with full tank
    
    // Calculate laps per stint using the same logic as stint calculation
    const maxLapsPerStint = Math.floor(fuelCapacity / fuelConsumption);
    const lapsPerStint = Math.min(maxLapsPerStint, Math.ceil(totalLaps / (pitStops + 1)));
    
    // let _remainingLaps = totalLaps; // Unused
    let currentStint = 1;
    
    for (let lap = 1; lap <= totalLaps; lap++) {
      const lapStartTime = currentTime;
      const lapEndTime = currentTime + avgLapTime;
      
      // Consume fuel for this lap
      currentFuel -= fuelConsumption;
      
      // Check if we need to pit after this lap
      // Pit when we've completed a full stint OR when we're running low on fuel
      const lapsInCurrentStint = lap - ((currentStint - 1) * lapsPerStint);
      const isPitLap = (lapsInCurrentStint >= lapsPerStint && pitStopCount < pitStops) || 
                      (currentFuel < fuelConsumption && pitStopCount < pitStops);
      
      if (isPitLap) {
        currentTime = lapEndTime + pitStopTime;
        pitStopCount++;
        currentStint++;
        
        // Calculate how much fuel we need for the remaining laps
        const lapsRemaining = totalLaps - lap;
        const fuelNeeded = lapsRemaining * fuelConsumption;
        const safetyMargin = 0.6; // 0.6L safety margin (car slows down below this)
        const requiredFuel = fuelNeeded + safetyMargin;
        
        // Refuel to the required amount, but not more than tank capacity
        currentFuel = Math.min(requiredFuel, fuelCapacity);
      } else {
        currentTime = lapEndTime;
      }
      
      breakdown.push({
        Strategy: strategyName,
        Lap: lap,
        "Start Time": this.formatTimeWithMS(lapStartTime),
        "End Time": this.formatTimeWithMS(lapEndTime),
        "Lap Duration": this.formatTimeWithMS(avgLapTime),
        "Pit Stop": isPitLap ? `Yes (${this.formatTimeWithMS(pitStopTime)})` : "No",
        "Fuel Left": `${currentFuel.toFixed(1)}L`,
        "Accumulated Time": this.formatTimeWithMS(currentTime),
        "Race Time Remaining": this.formatTimeWithMS((6 * 60 * 60) - currentTime), // 6 hours in seconds
        "White Flag": this.shouldShowWhiteFlag(currentTime, avgLapTime, 6 * 60 * 60)
      });
    }
    
    // Add the final lap after white flag if applicable
    const lastCrossingTime = currentTime;
    const timeRemaining = (6 * 60 * 60) - lastCrossingTime;
    const canCompleteOneMoreLap = timeRemaining >= avgLapTime;
    
    if (canCompleteOneMoreLap) {
      const finalLapStartTime = lastCrossingTime;
      const finalLapEndTime = lastCrossingTime + avgLapTime;
      
      breakdown.push({
        Strategy: strategyName,
        Lap: totalLaps + 1,
        "Start Time": this.formatTimeWithMS(finalLapStartTime),
        "End Time": this.formatTimeWithMS(finalLapEndTime),
        "Lap Duration": this.formatTimeWithMS(avgLapTime),
        "Pit Stop": "No",
        "Fuel Left": `${(currentFuel - fuelConsumption).toFixed(1)}L`,
        "Accumulated Time": this.formatTimeWithMS(finalLapEndTime),
        "Race Time Remaining": "00:00:00.000",
        "White Flag": "Shown at " + this.formatTimeWithMS(lastCrossingTime)
      });
    } else {
      // Add the final crossing after white flag
      const finalCrossingTime = lastCrossingTime + avgLapTime;
      
      breakdown.push({
        Strategy: strategyName,
        Lap: totalLaps + 1,
        "Start Time": this.formatTimeWithMS(lastCrossingTime),
        "End Time": this.formatTimeWithMS(finalCrossingTime),
        "Lap Duration": this.formatTimeWithMS(avgLapTime),
        "Pit Stop": "No",
        "Accumulated Time": this.formatTimeWithMS(finalCrossingTime),
        "Race Time Remaining": "00:00:00.000",
        "White Flag": "Shown at " + this.formatTimeWithMS(lastCrossingTime)
      });
    }
    
    return breakdown;
  } */

  /**
   * Format time in seconds to HH:MM:SS.MSMSMS format with milliseconds
   * @param seconds - Time in seconds
   * @returns Formatted time string with milliseconds
   */
  /* private formatTimeWithMS(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  }

  /**
   * Check if white flag should be shown at this crossing
   * @param crossingTime - Time when leader crosses finish line
   * @param lapTime - Time needed for one complete lap
   * @param raceDuration - Total race duration in seconds
   * @returns String indicating white flag status
   */
  /* private shouldShowWhiteFlag(crossingTime: number, lapTime: number, raceDuration: number): string {
    const timeRemaining = raceDuration - crossingTime;
    const canCompleteOneMoreLap = timeRemaining >= lapTime;
    return canCompleteOneMoreLap ? "No" : "Yes";
  } */
}

/**
 * Single Responsibility Principle: Calculates total race time
 * This class aggregates stint times and pit stop times
 */
export class TotalTimeCalculator {
  /**
   * Calculate the total race time including all stints and pit stops
   * @param stintBreakdown - Detailed breakdown of all stints
   * @returns Total race time in seconds
   */
  calculateTotalTime(
    stintBreakdown: StintInfo[]
  ): number {
    return stintBreakdown.reduce((total, stint) => {
      return total + stint.time + stint.pitStopTime;
    }, 0);
  }
}

/**
 * Open/Closed Principle: Main strategy calculator that orchestrates all calculations
 * This class is open for extension but closed for modification
 */
export class StrategyCalculator {
  // Removed unused calculateLastStintAnalysis testing helper

  /**
   * Calculate detailed lap-by-lap breakdown for testing purposes
   * @param totalLaps - Total laps in the race
   * @param avgLapTime - Average lap time in seconds
   * @param pitStopTime - Time per pit stop in seconds
   * @param pitStops - Number of pit stops
   * @param strategyName - Name of the strategy for display
   * @returns Array of lap details
   */
  /* private _calculateLapBreakdown(
    totalLaps: number,
    avgLapTime: number,
    pitStopTime: number,
    pitStops: number,
    strategyName: string,
    fuelCapacity: number = 120,
    fuelConsumption: number = 3.2
  ): any[] {
    const breakdown = [];
    let currentTime = 0; // Start at 00:00:00
    let pitStopCount = 0;
    let currentFuel = fuelCapacity; // Start with full tank
    const lapsPerStint = Math.ceil(totalLaps / (pitStops + 1)); // Calculate laps per stint
    
    for (let lap = 1; lap <= totalLaps; lap++) {
      const lapStartTime = currentTime;
      const lapEndTime = currentTime + avgLapTime;
      
      // Consume fuel for this lap
      currentFuel -= fuelConsumption;
      
      // Check if this lap ends with a pit stop (after every stint except the last)
      const isPitLap = lap % lapsPerStint === 0 && pitStopCount < pitStops && lap < totalLaps;
      
      if (isPitLap) {
        currentTime = lapEndTime + pitStopTime;
        pitStopCount++;
        currentFuel = fuelCapacity; // Refuel to full tank
      } else {
        currentTime = lapEndTime;
      }
      
      breakdown.push({
        Strategy: strategyName,
        Lap: lap,
        "Start Time": this.formatTimeWithMS(lapStartTime),
        "End Time": this.formatTimeWithMS(lapEndTime),
        "Lap Duration": this.formatTimeWithMS(avgLapTime),
        "Pit Stop": isPitLap ? `Yes (${this.formatTimeWithMS(pitStopTime)})` : "No",
        "Fuel Left": `${currentFuel.toFixed(1)}L`,
        "Accumulated Time": this.formatTimeWithMS(currentTime),
        "Race Time Remaining": this.formatTimeWithMS((6 * 60 * 60) - currentTime), // 6 hours in seconds
        "White Flag": this.shouldShowWhiteFlag(currentTime, avgLapTime, 6 * 60 * 60)
      });
    }
    
    // Add the final lap after white flag if applicable
    const lastCrossingTime = currentTime;
    const timeRemaining = (6 * 60 * 60) - lastCrossingTime;
    const canCompleteOneMoreLap = timeRemaining >= avgLapTime;
    
    if (canCompleteOneMoreLap) {
      const finalLapStartTime = lastCrossingTime;
      const finalLapEndTime = lastCrossingTime + avgLapTime;
      
      breakdown.push({
        Strategy: strategyName,
        Lap: totalLaps + 1,
        "Start Time": this.formatTimeWithMS(finalLapStartTime),
        "End Time": this.formatTimeWithMS(finalLapEndTime),
        "Lap Duration": this.formatTimeWithMS(avgLapTime),
        "Pit Stop": "No",
        "Accumulated Time": this.formatTimeWithMS(finalLapEndTime),
        "Race Time Remaining": "00:00:00.000",
        "White Flag": "Shown at " + this.formatTimeWithMS(lastCrossingTime)
      });
    } else {
      // Add the final crossing after white flag
      const finalCrossingTime = lastCrossingTime + avgLapTime;
      
      breakdown.push({
        Strategy: strategyName,
        Lap: totalLaps + 1,
        "Start Time": this.formatTimeWithMS(lastCrossingTime),
        "End Time": this.formatTimeWithMS(finalCrossingTime),
        "Lap Duration": this.formatTimeWithMS(avgLapTime),
        "Pit Stop": "No",
        "Accumulated Time": this.formatTimeWithMS(finalCrossingTime),
        "Race Time Remaining": "00:00:00.000",
        "White Flag": "Shown at " + this.formatTimeWithMS(lastCrossingTime)
      });
    }
    
    return breakdown;
  } */

  /**
   * Format time in seconds to HH:MM:SS format
   * @param seconds - Time in seconds
   * @returns Formatted time string
   */
  /* private _formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } */

  /**
   * Format time in seconds to HH:MM:SS.MSMSMS format with milliseconds
   * @param seconds - Time in seconds
   * @returns Formatted time string with milliseconds
   */
  /* private formatTimeWithMS(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  }

  /**
   * Check if white flag should be shown at this crossing
   * @param crossingTime - Time when leader crosses finish line
   * @param lapTime - Time needed for one complete lap
   * @param raceDuration - Total race duration in seconds
   * @returns String indicating white flag status
   */
  /* private shouldShowWhiteFlag(crossingTime: number, lapTime: number, raceDuration: number): string {
    const timeRemaining = raceDuration - crossingTime;
    const canCompleteOneMoreLap = timeRemaining >= lapTime;
    return canCompleteOneMoreLap ? "No" : "Yes";
  } */

  private lapCalculator: LapCalculator;
  private stintCalculator: StintCalculator;
  private fuelSaveCalculator: FuelSaveCalculator;
  // private _totalTimeCalculator: TotalTimeCalculator; // Unused

  constructor() {
    this.lapCalculator = new LapCalculator();
    this.stintCalculator = new StintCalculator();
    this.fuelSaveCalculator = new FuelSaveCalculator();
    // this._totalTimeCalculator = new TotalTimeCalculator(); // Unused
  }

    /**
   * Calculate complete race strategy for given parameters
   * @param raceEvent - Race event configuration
   * @param driverData - Driver performance data
   * @param selectedCarClass - Selected car class ID
   * @param userActualLapTime - User's actual achievable lap time (optional)
   * @param userActualFuelConsumption - User's actual fuel consumption when fuel saving (optional)
   * @returns Complete strategy calculation
   */
  calculateStrategy(
    raceEvent: RaceEvent,
    driverData: DriverData,
    selectedCarClass: string,
    userActualLapTime?: number | null,
    userActualFuelConsumption?: number | null
): Strategy {
    // Find the selected car class
    const carClass = raceEvent.carClasses.find(c => c.id === selectedCarClass);
    if (!carClass) {
      throw new Error(`Car class ${selectedCarClass} not found`);
    }

    // Calculate effective fuel consumption with traffic adjustments
    const trafficImpactMultipliers = [1.0, 1.02, 1.03, 1.05]; // None, Light, Moderate, Heavy
    const trafficMultiplier = trafficImpactMultipliers[driverData.trafficImpact || 0];
    const manualAdjustment = driverData.trafficFuelAdjustment || 0;
    const effectiveFuelConsumption = (driverData.fuelConsumption * trafficMultiplier) + manualAdjustment;
    
    // Debug: Traffic fuel adjustment (removed for production)
    
    // First, calculate laps per stint to estimate pit stops (basic, no safety)
    const lapsPerStint = this.lapCalculator.calculateLapsPerStint(
      driverData.fuelCapacity, 
      effectiveFuelConsumption
    );
    
    // Debug: Laps per stint calculation (removed for production)
    
    // Prepare pit stop details for accurate white-flag simulation
    const calculatedPitStopDetails = {
      ...driverData.pitStopDetails,
      totalTime: driverData.pitStopDetails.totalTime
    };

    // SIMPLIFIED: Always assume we are the leading car
    // Use the driver's own average lap time for race end calculation
    const driverLapTime = driverData.avgLapTime;

    // Enforce 0.6L safety in stint sizing for timeline simulation (car slows down below this)
    const safetyMargin = 0.6;
    const driverUsableFuel = Math.max(0, driverData.fuelCapacity - safetyMargin);
    const driverLapsPerStint = Math.max(1, Math.floor(driverUsableFuel / effectiveFuelConsumption));
    const pitStopTimeFull = calculatedPitStopDetails.pitInTime + calculatedPitStopDetails.fuelingTime + calculatedPitStopDetails.pitOutTime;

    // Simulate driver's timeline to detect white flag and final lap
    const raceSeconds = raceEvent.duration * 60;
    let simTime = 0;
    let simLaps = 0;
    while (true) {
      // If starting a new lap and remaining time is less than lap time, this is the white flag (final) lap
      if ((raceSeconds - simTime) < driverLapTime) {
        simTime += driverLapTime; // complete the final lap
        simLaps += 1;
        break;
      }
      // Complete another full lap
      simTime += driverLapTime;
      simLaps += 1;
      // Add pit stop time after each full stint, except we don't pit after the final lap
      if (simLaps % driverLapsPerStint === 0) {
        simTime += pitStopTimeFull;
      }
    }

    const totalLaps = simLaps;
    
    // Calculate stints and pit stops
    const stints = this.stintCalculator.calculateStints(totalLaps, lapsPerStint);
  const pitStops = stints - 1;

    // Debug: Strategy calc summary (removed for production)

    // Calculate detailed last stint analysis for normal strategy
    /* const normalStrategyLastStintAnalysis = this.calculateLastStintAnalysis(
      totalLaps,
      driverData.avgLapTime,
      driverData.pitStopDetails.totalTime,
      pitStops,
      "Normal Strategy"
    ); */
    
    // Debug: Normal strategy last stint analysis (removed for production)

    // Note: Detailed lap-by-lap breakdown is now available in the expandable stint breakdown in the UI

    // totalLaps already includes the final lap after white flag
    const actualTotalLaps = totalLaps;
    
    // Create detailed stint breakdown with properly calculated pit stop details
    
    // Debug: Pit stop details (removed for production)
    
    // Debug: Stint breakdown calculation inputs (removed for production)
    
    const stintBreakdown = this.stintCalculator.calculateStintBreakdown(
      actualTotalLaps,
      lapsPerStint,
      driverData.avgLapTime,
      effectiveFuelConsumption,
      driverData.fuelCapacity,
      calculatedPitStopDetails
    );
    
    // CORRECTED: Calculate total race time from actual stint breakdown
    // This accounts for the actual refueling times, not estimated full pit stops
    const totalStintTime = stintBreakdown.reduce((sum, stint) => sum + stint.time, 0);
    const totalPitTime = stintBreakdown.reduce((sum, stint) => sum + stint.pitStopTime, 0);
    const actualTotalTime = totalStintTime + totalPitTime;
    
    // Debug: Stint breakdown result (removed for production)
    
    // Calculate fuel save analysis
    // Debug: Fuel save calculation inputs (removed for production)
    
    // Pass the actual race end time (in minutes) from the leader simulation
    // This ensures fuel save strategy accounts for when the race actually ends
    const actualRaceEndTimeMinutes = simTime / 60;
    
    const fuelSave = this.fuelSaveCalculator.calculateFuelSaveStrategy(
      actualRaceEndTimeMinutes,
      totalLaps,
      lapsPerStint,
      stints,
      effectiveFuelConsumption,
      calculatedPitStopDetails,
      driverData.avgLapTime,
      driverData.fuelCapacity,
      userActualLapTime,
      userActualFuelConsumption
    );
    
    // Debug: Fuel save calculation result (removed for production)

    return {
      totalLaps: actualTotalLaps,
      lapsPerStint,
      stints,
      pitStops,
      totalTime: actualTotalTime, // Use actual total from stint breakdown
      fuelSave,
      stintBreakdown
    };
  }
}

/**
 * Dependency Inversion Principle: Interface for strategy calculation
 * High-level modules depend on this interface, not concrete implementations
 */
export interface IStrategyCalculator {
  calculateStrategy(raceEvent: RaceEvent, driverData: DriverData, selectedCarClass: string, userActualLapTime?: number | null, userActualFuelConsumption?: number | null): Strategy;
}

/**
 * Factory for creating strategy calculator instances
 * Follows the Factory pattern for dependency injection
 */
export class StrategyCalculatorFactory {
  /**
   * Create a new strategy calculator instance
   * @returns Strategy calculator implementing the interface
   */
  static create(): IStrategyCalculator {
    return new StrategyCalculator();
  }
}
